<script lang="ts">
import { templateRef } from '@vueuse/core'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { computed, defineComponent, inject, onUnmounted, ref, watch } from 'vue'

import { loaderInjectionKey } from '@/App.vue'

export default defineComponent({
  name: 'ScrollProgress',
  setup (props, { attrs, slots, emit }) {
    const loader = inject(loaderInjectionKey)
    const wrapper = templateRef<HTMLElement>('wrapper')
    const progress1 = ref(0) // green
    const progress2 = ref(1) // blue
    const mini = computed(() => loader.value ? loader.value.visible && !loader.value.leaving : true)

    const tl = gsap.timeline({
      scrollTrigger: {
        // trigger: document.documentElement,
        start: 'top top',
        end: 'bottom bottom',
        scrub: true
      }
    }).fromTo(progress1, { value: 0 }, { value: 1, ease: 'none' }, 0)
      .fromTo(progress2, { value: 1 }, { value: 0, ease: 'power1.inOut' }, 0)

    onUnmounted(() => tl.kill())

    watch([wrapper, mini], ([target, _mini]) => {
      if (!target) return
      ScrollTrigger.refresh()
      gsap.to(target, {
        width: _mini ? '0%' : '100%',
        duration: 1.5,
        ease: 'power4.inOut'
      })
    })

    return {
      loader,
      mini,
      progress1,
      progress2
    }
  }
})
</script>

<template>
  <div
    class="wrapper fixed bottom-0 left-0 h-2 pointer-events-none"
    :class="{ 'opacity-0': !$route.meta?.scrollProgress }"
  >
    <div ref="wrapper" class="flex items-center h-full w-0">
      <div class="relative w-[20%] h-full bg-green" />
      <div class="relative flex-auto h-full">
        <div class="absolute inset-0 bg-black" />
        <div class="progress1 w-full h-full bg-green" />
      </div>
      <div class="progress2 absolute right-0 w-[20%] h-full bg-blue" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  z-index: $z-scroll-progress;
  right: var(--scrollbar-placeholder);
  transition: opacity var(--transition-duration);
}

.progress1 {
  transform: scaleX(v-bind(progress1));
  transform-origin: center left;
  transition: transform linear 0.15s;
}

.progress2 {
  transform: scaleX(v-bind(progress2));
  transform-origin: center right;
  transition: transform linear 0.15s;
}
</style>
